/**
 * InfinitSales - Connection Modal
 * QR Code modal for WhatsApp connection
 * 
 * VERSÃO SIMPLIFICADA:
 * - Polling direto no /api/whatsapp/status a cada 2 segundos
 * - Sem dependência de SSE
 * - Simples e funcional
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { X, RefreshCw, Loader2, Smartphone, CheckCircle2, WifiOff, Settings } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import whatsappService from '../../services/wa-personal/whatsappService';
import ProviderPicker from './ProviderPicker';

const ConnectionModal = ({ isOpen, onClose, onConnected }) => {
  const { colors } = useTheme();
  const [qrCode, setQrCode] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [status, setStatus] = useState('loading'); // loading, waiting, connected, error, recreating
  const [isRecreating, setIsRecreating] = useState(false);
  // Provedores de WhatsApp QR (2026-09-16): a plataforma pode ter mais de um
  // motor de conexao (padrao + alternativas). O usuario escolhe por aqui;
  // trocar = recriar a instancia no motor escolhido e ler o QR de novo.
  const [providers, setProviders] = useState([]);
  const [providerSelectable, setProviderSelectable] = useState(false);
  const [currentProvider, setCurrentProvider] = useState(null);
  // true quando o modal abriu porque o WhatsApp CAIU (ja esteve conectado):
  // o picker mostra o aviso e oferece trocar de provedor.
  const [disconnected, setDisconnected] = useState(false);
  
  // Refs
  const pollingRef = useRef(null);
  const isMountedRef = useRef(true);

  // ============================================
  // STYLES
  // ============================================
  const styles = {
    overlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
    },
    modal: {
      backgroundColor: colors.bgPrimary,
      borderRadius: '12px',
      maxWidth: '420px',
      width: '90%',
      maxHeight: '90vh',
      overflowY: 'auto',
      position: 'relative',
    },
    closeBtn: {
      position: 'absolute',
      top: '12px',
      right: '12px',
      width: '32px',
      height: '32px',
      border: 'none',
      background: 'transparent',
      color: colors.textSecondary,
      cursor: 'pointer',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    content: {
      padding: '32px',
    },
    header: {
      textAlign: 'center',
      marginBottom: '24px',
    },
    headerIcon: {
      color: colors.accentPrimary,
      marginBottom: '12px',
    },
    headerTitle: {
      fontSize: '20px',
      color: colors.textPrimary,
      marginBottom: '8px',
      margin: 0,
    },
    headerText: {
      color: colors.textSecondary,
      fontSize: '14px',
      margin: 0,
    },
    qrContainer: {
      display: 'flex',
      justifyContent: 'center',
      marginBottom: '24px',
    },
    qrCode: {
      width: '256px',
      height: '256px',
      borderRadius: '8px',
    },
    qrLoading: {
      width: '256px',
      height: '256px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: colors.bgTertiary,
      borderRadius: '8px',
      color: colors.textSecondary,
      gap: '16px',
      textAlign: 'center',
    },
    qrError: {
      width: '256px',
      height: '256px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: colors.bgTertiary,
      borderRadius: '8px',
      color: colors.textSecondary,
      gap: '16px',
      textAlign: 'center',
      padding: '16px',
    },
    qrRecreating: {
      width: '256px',
      height: '256px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: colors.bgTertiary,
      borderRadius: '8px',
      color: colors.accentPrimary,
      gap: '16px',
      textAlign: 'center',
      padding: '16px',
    },
    steps: {
      display: 'flex',
      flexDirection: 'column',
      gap: '12px',
    },
    step: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      fontSize: '14px',
      color: colors.textSecondary,
    },
    stepNumber: {
      width: '24px',
      height: '24px',
      borderRadius: '50%',
      backgroundColor: colors.accentPrimary,
      color: 'white',
      fontSize: '12px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
    },
    footer: {
      marginTop: '24px',
      textAlign: 'center',
    },
    successContainer: {
      textAlign: 'center',
      padding: '24px',
    },
    successIcon: {
      color: colors.accentPrimary,
      marginBottom: '16px',
    },
    successTitle: {
      color: colors.textPrimary,
      marginBottom: '8px',
    },
    successText: {
      color: colors.textSecondary,
    },
    btnSecondary: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '8px',
      padding: '10px 20px',
      border: `1px solid ${colors.borderColor}`,
      backgroundColor: colors.bgPrimary,
      color: colors.textPrimary,
      borderRadius: '8px',
      cursor: 'pointer',
      fontSize: '14px',
    },
    btnGhost: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '8px',
      padding: '10px 20px',
      border: 'none',
      background: 'transparent',
      color: colors.accentPrimary,
      cursor: 'pointer',
      fontSize: '14px',
    },
    recreatingText: {
      fontSize: '14px',
      color: colors.textSecondary,
      marginTop: '8px',
    },
  };

  // ============================================
  // STOP POLLING
  // ============================================
  const stopPolling = useCallback(() => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  }, []);

  // ============================================
  // CHECK CONNECTION STATUS (polling)
  // ============================================
  const checkStatus = useCallback(async () => {
    if (!isMountedRef.current) return;
    
    try {
      const result = await whatsappService.getConnectionStatus();
      const data = result?.data || result;
      
      console.log('[ConnectionModal] Status:', data?.status, '| Connected:', data?.connected);
      
      if (data?.connected) {
        console.log('[ConnectionModal] ✅ CONECTADO! Fechando modal...');
        stopPolling();
        setStatus('connected');
        
        setTimeout(() => {
          if (isMountedRef.current) {
            onConnected?.();
          }
        }, 1500);
      }
    } catch (err) {
      console.error('[ConnectionModal] Erro no polling:', err);
    }
  }, [stopPolling, onConnected]);

  // ============================================
  // START POLLING
  // ============================================
  const startPolling = useCallback(() => {
    stopPolling();
    
    // Poll a cada 2 segundos
    pollingRef.current = setInterval(checkStatus, 2000);
    console.log('[ConnectionModal] Polling iniciado (2s)');
  }, [stopPolling, checkStatus]);

  // ============================================
  // FETCH QR CODE
  // ============================================
  const fetchQRCode = useCallback(async () => {
    if (!isMountedRef.current) return;
    
    setLoading(true);
    setError('');
    setStatus('loading');

    try {
      // Primeiro verifica se já está conectado
      const statusResult = await whatsappService.getConnectionStatus();
      const statusData = statusResult?.data || statusResult;
      
      if (statusData?.connected) {
        setStatus('connected');
        setTimeout(() => onConnected?.(), 1500);
        return;
      }
      // Caiu (ja teve numero pareado) ou sessao fantasma: o picker pergunta
      // se quer tentar outro provedor.
      setDisconnected(Boolean(statusData?.session_closed || statusData?.phone_number || statusData?.last_connected_at));

      // Busca QR Code
      const qrResult = await whatsappService.getQRCode();
      const qrData = qrResult?.data || qrResult;
      
      if (!isMountedRef.current) return;

      if (qrData?.connected) {
        setStatus('connected');
        setTimeout(() => onConnected?.(), 1500);
        return;
      }
      
      if (qrData?.qrcode) {
        setQrCode(qrData.qrcode);
        setStatus('waiting');
        startPolling(); // Inicia polling quando tem QR
      } else {
        setError('Não foi possível gerar o QR Code');
        setStatus('error');
      }
    } catch (err) {
      console.error('[ConnectionModal] Erro:', err);
      if (isMountedRef.current) {
        setError('Erro ao gerar QR Code');
        setStatus('error');
      }
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  }, [onConnected, startPolling]);

  // ============================================
  // RECREATE INSTANCE
  // ============================================
  const handleRecreateInstance = useCallback(async (providerId = null) => {
    if (!isMountedRef.current) return;
    
    setIsRecreating(true);
    setError('');
    setStatus('recreating');
    stopPolling();

    try {
      const result = await whatsappService.recreateInstance(providerId || null);
      const data = result?.data || result;
      
      if (!isMountedRef.current) return;
      if (providerId) setCurrentProvider(providerId);
      
      if (data?.qrcode) {
        setQrCode(data.qrcode);
        setStatus('waiting');
        startPolling();
      } else if (data?.connected) {
        setStatus('connected');
        setTimeout(() => onConnected?.(), 1500);
      } else {
        setError('Não foi possível reconfigurar');
        setStatus('error');
      }
    } catch (err) {
      console.error('[ConnectionModal] Erro recreate:', err);
      if (isMountedRef.current) {
        setError('Erro ao reconfigurar');
        setStatus('error');
      }
    } finally {
      if (isMountedRef.current) {
        setIsRecreating(false);
      }
    }
  }, [stopPolling, startPolling, onConnected]);

  // ============================================
  // RETRY
  // ============================================
  const handleSelectProvider = useCallback(async (providerId) => {
    if (!providerId || providerId === currentProvider || isRecreating) return;
    await handleRecreateInstance(providerId);
  }, [currentProvider, isRecreating, handleRecreateInstance]);

  const loadProviders = useCallback(async () => {
    try {
      const result = await whatsappService.getProviders();
      const data = result?.data || result || {};
      if (!isMountedRef.current) return;
      setProviders(Array.isArray(data.providers) ? data.providers : []);
      setProviderSelectable(Boolean(data.selectable));
      setCurrentProvider(data.current || data.default || null);
    } catch (err) {
      // Sem o endpoint (backend antigo) a tela segue como sempre, sem seletor.
      console.debug('[ConnectionModal] providers indisponiveis:', err?.message);
    }
  }, []);

  const handleRetry = useCallback(async () => {
    if (status === 'error') {
      await handleRecreateInstance();
    } else {
      await fetchQRCode();
    }
  }, [status, handleRecreateInstance, fetchQRCode]);

  // ============================================
  // EFFECTS
  // ============================================
  
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      stopPolling();
    };
  }, [stopPolling]);

  // Quando modal abre
  useEffect(() => {
    if (isOpen) {
      setQrCode(null);
      setError('');
      setStatus('loading');
      setLoading(true);
      loadProviders();
      fetchQRCode();
    } else {
      stopPolling();
    }
    
    return () => stopPolling();
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // Refresh QR a cada 30s
  useEffect(() => {
    if (!isOpen || status !== 'waiting') return;
    
    const timeout = setTimeout(async () => {
      // Verifica status antes de pedir novo QR
      const result = await whatsappService.getConnectionStatus();
      const data = result?.data || result;
      
      if (data?.connected) {
        stopPolling();
        setStatus('connected');
        setTimeout(() => onConnected?.(), 1500);
      } else {
        fetchQRCode();
      }
    }, 30000);
    
    return () => clearTimeout(timeout);
  }, [isOpen, status, qrCode]); // eslint-disable-line react-hooks/exhaustive-deps

  // ============================================
  // RENDER
  // ============================================
  if (!isOpen) return null;

  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>
        <button 
          style={styles.closeBtn} 
          onClick={onClose} 
          disabled={isRecreating || status === 'connected'}
        >
          <X size={20} />
        </button>

        <div style={styles.content}>
          {status === 'connected' ? (
            <div style={styles.successContainer}>
              <div style={styles.successIcon}>
                <CheckCircle2 size={64} />
              </div>
              <h3 style={styles.successTitle}>Conectado com sucesso!</h3>
              <p style={styles.successText}>Seu WhatsApp está pronto para usar</p>
            </div>
          ) : (
            <>
              <div style={styles.header}>
                <div style={styles.headerIcon}>
                  <Smartphone size={32} />
                </div>
                <h3 style={styles.headerTitle}>Conectar WhatsApp</h3>
                <p style={styles.headerText}>Escaneie o QR Code com seu celular</p>
              </div>

              <div style={styles.qrContainer}>
                {status === 'recreating' ? (
                  <div style={styles.qrRecreating}>
                    <Settings size={48} style={{ animation: 'spin 2s linear infinite' }} />
                    <span style={{ fontWeight: '500' }}>Reconfigurando seu ambiente...</span>
                    <span style={styles.recreatingText}>Isso pode levar alguns segundos</span>
                  </div>
                ) : loading ? (
                  <div style={styles.qrLoading}>
                    <Loader2 size={48} style={{ animation: 'spin 1s linear infinite' }} />
                    <span>Gerando QR Code...</span>
                  </div>
                ) : error ? (
                  <div style={styles.qrError}>
                    <WifiOff size={48} />
                    <span>{error}</span>
                    <button 
                      style={styles.btnSecondary} 
                      onClick={handleRetry}
                      disabled={isRecreating}
                    >
                      <RefreshCw size={16} />
                      Tentar novamente
                    </button>
                  </div>
                ) : qrCode ? (
                  <img
                    src={qrCode.startsWith('data:') ? qrCode : `data:image/png;base64,${qrCode}`}
                    alt="QR Code"
                    style={styles.qrCode}
                  />
                ) : null}
              </div>

              {status === 'waiting' && (
                <>
                  <div style={styles.steps}>
                    <div style={styles.step}>
                      <span style={styles.stepNumber}>1</span>
                      <span>Abra o WhatsApp no seu celular</span>
                    </div>
                    <div style={styles.step}>
                      <span style={styles.stepNumber}>2</span>
                      <span>Toque em Menu ou Configurações</span>
                    </div>
                    <div style={styles.step}>
                      <span style={styles.stepNumber}>3</span>
                      <span>Selecione "Aparelhos conectados"</span>
                    </div>
                    <div style={styles.step}>
                      <span style={styles.stepNumber}>4</span>
                      <span>Escaneie este QR Code</span>
                    </div>
                  </div>

                  <div style={styles.footer}>
                    <button style={styles.btnGhost} onClick={fetchQRCode}>
                      <RefreshCw size={16} />
                      Gerar novo QR Code
                    </button>
                  </div>
                </>
              )}

              {providerSelectable && (status === 'waiting' || status === 'error') && (
                <ProviderPicker
                  providers={providers}
                  current={currentProvider}
                  disabled={isRecreating}
                  disconnected={disconnected}
                  defaultOpen={status === 'error'}
                  onSelect={handleSelectProvider}
                />
              )}
            </>
          )}
        </div>
      </div>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default ConnectionModal;